import React, {useLayoutEffect} from "react";
import {Redirect, Route, Switch, useHistory, useLocation, useParams} from "react-router-dom";
import {routeFunctions, routes} from "../../../../../core/models/static/routes/app-routes";
import FreeShippingFeesManagementView from "./free-shipping-fees-management";
import ShippingFeesListView from "./list";
import {matchPath} from "react-router";
import ShippingFeesCompaniesListView from "./companies";
import ShippingFeesReportsView from "./reports";
import ShippingRatesView from "./shipping-rates";
import useAccessLevels from "../../../../hooks/use-access-levels";

const ShippingFeesViews = () => {
    const operatorAccessLevels = useAccessLevels();
    const location = useLocation();
    const history = useHistory();
    const params = useParams();
    const canShippingRates = operatorAccessLevels.packages.sales.regionRates
        || operatorAccessLevels.packages.sales.partRates;

    /**
     * With each change in the location:
     * - if the location is [routes.shippingFees.base] then changes it to the first available child
     * - if the location is [routes.customer.shippingFees.base] then changes it to the first available customer child
     * - redirects the legacy region-rates path to the nested shipping-rates region child
     */
    useLayoutEffect(() => {
        if (matchPath(location.pathname, {path: routes.shippingFees.regionRates, exact: true})) {
            return history.replace(routes.shippingFees.shippingRates.regionRates);
        }
        if (matchPath(location.pathname, {path: routes.shippingFees.base, exact: true})) {
            if (operatorAccessLevels.packages.sales.freightFees) {
                return history.replace(routes.shippingFees.list);
            } else if (operatorAccessLevels.packages.sales.freeFreightFees) {
                return history.replace(routes.shippingFees.settings);
            } else if (operatorAccessLevels.packages.sales.customerFreightFees) {
                return history.replace(routes.shippingFees.list);
            } else if (operatorAccessLevels.packages.sales.freightReports) {
                return history.replace(routes.shippingFees.reports);
            } else if (canShippingRates) {
                return history.replace(routes.shippingFees.shippingRates.base);
            } else {
                return history.replace(routes.shippingFees.base.concat(routes.error.serverErrorForSection));
            }
        }
        if (matchPath(location.pathname, {path: routes.customer.shippingFees.base, exact: true})) {
            if (operatorAccessLevels.packages.sales.customerFreightFees) {
                return history.replace(routeFunctions.customer.shippingFees.list(params.customerId));
            } else if (operatorAccessLevels.packages.sales.customerFreeFreightFees) {
                return history.replace(routeFunctions.customer.shippingFees.settings(params.customerId));
            } else {
                return history.replace(routes.customer.shippingFees.base.concat(routes.error.serverErrorForSection));
            }
        }
    }, [
        location,
        params.customerId,
        history,
        canShippingRates,
        operatorAccessLevels.packages.sales.freightFees,
        operatorAccessLevels.packages.sales.freeFreightFees,
        operatorAccessLevels.packages.sales.customerFreightFees,
        operatorAccessLevels.packages.sales.freightReports,
        operatorAccessLevels.packages.sales.customerFreeFreightFees,
    ])

    return (
        <>
            <Switch>
                <Route path={routes.shippingFees.base} exact/>
                <Route path={routes.customer.shippingFees.base} exact/>
                <Route path={routes.shippingFees.regionRates} exact/>
                {
                    (
                        operatorAccessLevels.packages.sales.freightFees ||
                        operatorAccessLevels.packages.sales.customerFreightFees
                    ) &&
                    <Route path={[
                        ...(operatorAccessLevels.packages.sales.freightFees ? [routes.shippingFees.list] : []),
                        ...(operatorAccessLevels.packages.sales.customerFreightFees ? [routes.customer.shippingFees.list] : []),
                    ]} exact>
                        <ShippingFeesListView/>
                    </Route>
                }
                {
                    (
                        operatorAccessLevels.packages.sales.customerFreeFreightFees ||
                        operatorAccessLevels.packages.sales.freeFreightFees
                    ) &&
                    <Route
                        path={[
                            ...(operatorAccessLevels.packages.sales.freeFreightFees ? [routes.shippingFees.settings] : []),
                            ...(operatorAccessLevels.packages.sales.customerFreeFreightFees ? [routes.customer.shippingFees.settings] : []),
                        ]}
                        exact>
                        <FreeShippingFeesManagementView/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.sales.freightCompanies &&
                    <Route path={routes.shippingFees.companies} exact>
                        <ShippingFeesCompaniesListView/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.sales.freightReports &&
                    <Route path={routes.shippingFees.reports} exact>
                        <ShippingFeesReportsView/>
                    </Route>
                }
                {
                    canShippingRates &&
                    <Route path={routes.shippingFees.shippingRates.base}>
                        <ShippingRatesView/>
                    </Route>
                }
                <Redirect to={routes.shippingFees.base.concat(routes.error.serverErrorForSection)}/>
            </Switch>
        </>
    )
}

export default ShippingFeesViews;
